import { z } from 'zod';

/** Apple/Google login: the OIDC ID token obtained natively on the device. */
export const OidcLoginInput = z.object({
  idToken: z.string().min(1),
  /**
   * Deliberate "create a separate account anyway" after a 409 `account_exists`.
   * Without it the server refuses to silently mint a second account for an
   * email that already has exactly one.
   */
  force: z.boolean().optional(),
});
export type OidcLoginInput = z.infer<typeof OidcLoginInput>;

/** Mirror of the Prisma `AuthProvider` enum. */
export const AuthProviderName = z.enum(['PHONE', 'APPLE', 'GOOGLE', 'TELEGRAM']);
export type AuthProviderName = z.infer<typeof AuthProviderName>;

/**
 * 409 body for Apple/Google login when the (platform-verified) email already
 * has exactly one account that can actually be logged into. `providers` lists
 * how — minus the one the caller just used. The client offers those, plus an
 * explicit "create a new account" that retries with `force: true`.
 */
export const AccountExistsConflict = z.object({
  code: z.literal('account_exists'),
  providers: z.array(AuthProviderName),
});
export type AccountExistsConflict = z.infer<typeof AccountExistsConflict>;

/** Register a push-notification device token for the current user. */
export const RegisterDeviceInput = z.object({
  platform: z.enum(['IOS', 'ANDROID']),
  pushToken: z.string().min(1),
});
export type RegisterDeviceInput = z.infer<typeof RegisterDeviceInput>;

/**
 * Secret-gated mobile dev login (`POST /auth/dev/mobile`). Mints a session for
 * ONE fixed dev account; enabled only when the server has a matching
 * `DEV_LOGIN_SECRET` and never in production.
 *
 * Deliberately just the secret. The secret is inlined into the JS bundle of a
 * stage build and can be read out of the APK with `strings`, so anything else
 * accepted here is attacker-controlled: `telegramId` used to select WHICH user
 * to mint a session for — i.e. any existing account, admins included.
 */
export const DevMobileLoginInput = z.object({
  secret: z.string().min(1),
});
export type DevMobileLoginInput = z.infer<typeof DevMobileLoginInput>;

/** Mobile clients receive both tokens in the body (no cookie). */
export const MobileAuthResponse = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
});
export type MobileAuthResponse = z.infer<typeof MobileAuthResponse>;

/** Link-Telegram result: `claimed` = the OIDC identity was moved onto an
 * existing Telegram account and the caller is now that account. */
export const LinkTelegramResponse = MobileAuthResponse.extend({
  claimed: z.boolean(),
});
export type LinkTelegramResponse = z.infer<typeof LinkTelegramResponse>;
